import { TEAM_MANAGER } from 'constants/roles';
import { USER_FILTER_KEYS } from 'constants/filters';
import { UserTeam } from 'contexts/teams/types';
import { UserInvites } from 'contexts/users/types';

export interface IGenericUsersTableProps {
  accountPage?: boolean;
  refreshData?(): void;
}

export interface ITableColumn {
  name: string;
  subcolumns: string[];
  tooltip?: string;
  columnKey?: string;
}

export interface IUsersTableConfig {
  rowTableConfig: string;
  columns: ITableColumn[];
}

const ACTIONS_COLUMN_WIDTH = '104px';

export const getUsersTableConfig = (
  staffUser: boolean,
  accountPage: boolean,
  filterActive: boolean,
  skillDiveOnPending = false
): IUsersTableConfig => {
  if (!staffUser) {
    return {
      rowTableConfig: `40px minmax(0, 3fr) minmax(0, 2.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) ${ACTIONS_COLUMN_WIDTH}`,
      columns: [
        { name: 'Users', subcolumns: null, columnKey: 'user_display' },
        { name: 'Teams', subcolumns: null, columnKey: 'teams' },
        { name: 'License', subcolumns: null, columnKey: 'license' },
        (filterActive || skillDiveOnPending) && {
          name: 'Skill Dive',
          subcolumns: null,
          columnKey: USER_FILTER_KEYS.LICENSE_ADDONS,
        },
        {
          name: filterActive ? 'Last login' : 'Invited',
          subcolumns: null,
          tooltip: "User's last login to the platform",
          columnKey: filterActive ? 'user__last_login' : 'invited_at',
        },
      ],
    };
  } else if (staffUser && !accountPage) {
    return {
      rowTableConfig: `40px minmax(0, 3fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1fr) ${ACTIONS_COLUMN_WIDTH}`,
      columns: [
        { name: 'Users', subcolumns: null, columnKey: 'user_display' },
        { name: 'Account', subcolumns: null },
        { name: 'Owner', subcolumns: null },
        { name: 'Teams', subcolumns: null, columnKey: 'teams' },
        { name: 'License', subcolumns: ['Plan', 'Skill Dive'], columnKey: 'license' },
      ],
    };
  } else {
    return {
      rowTableConfig: `40px minmax(0, 3fr) minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1.2fr) minmax(0, 1fr) ${ACTIONS_COLUMN_WIDTH}`,
      columns: [
        { name: 'Users', subcolumns: null, columnKey: 'user_display' },
        { name: 'Owner', subcolumns: null },
        { name: 'Teams', subcolumns: null, columnKey: 'teams' },
        { name: 'License', subcolumns: ['Plan', 'Skill Dive'], columnKey: 'license' },
        {
          name: filterActive ? 'Last login' : 'Invited',
          subcolumns: null,
          tooltip: "User's last login to the platform",
          columnKey: filterActive ? 'user__last_login' : 'invited_at',
        },
      ],
    };
  }
};

export const isRoleManager = (role: string, teams: UserTeam[]): boolean => {
  return role === TEAM_MANAGER && Boolean(teams.length > 0);
};

// A team invite's target counts as a Team Manager by the invite role or the UAA roles, as the
// server checks both before refusing a license change.
export const isTeamManagerTarget = (
  user: Pick<UserInvites, 'role' | 'teams' | 'uaa_data'>
): boolean =>
  isRoleManager(user.role, user.teams ?? []) ||
  Boolean(user.uaa_data?.roles?.data?.some(({ name }) => name === TEAM_MANAGER));

export interface LicenseViewer {
  uaaId?: string;
  email?: string;
  isStaff: boolean;
  isAccountOwner: boolean;
}

// The user whose license would change.
export interface LicenseTarget {
  uaaId?: string;
  email?: string;
  isAccountOwner: boolean;
  isTeamManager: boolean;
}

export const LICENSE_ROLE_LOCK_TOOLTIPS = {
  accountOwner: "You cannot change the account owner's license",
  teamManager: "You cannot change another team manager's license",
};

// Why the signed-in user can't change this license: only the account owner changes their own,
// and only the account owner or the manager changes a Team Manager's. Staff can change any.
export const licenseRoleLock = (target: LicenseTarget, viewer: LicenseViewer): string | null => {
  if (viewer.isStaff) return null;
  const isViewer = target.uaaId ? target.uaaId === viewer.uaaId : target.email === viewer.email;
  if (isViewer) return null;
  if (target.isAccountOwner) return LICENSE_ROLE_LOCK_TOOLTIPS.accountOwner;
  if (target.isTeamManager && !viewer.isAccountOwner) return LICENSE_ROLE_LOCK_TOOLTIPS.teamManager;
  return null;
};
